import { Alert, Button, Container, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconLinkOff } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { formatDate, formatRecordingDuration } from './format';
import { RecordingPlayer } from './recording-player';
import { ApiError, recordingsApi, type SharedRecording } from './recordings-api';
import { i18n } from '@/i18n';
import { LanguageSwitcher } from '@/shared/language-switcher';
import { UserMenu } from '@/shared/user-menu';

type SharedState =
    | { kind: 'loading' }
    | { kind: 'ready'; shared: SharedRecording }
    | { kind: 'notFound' }
    | { kind: 'error'; message: string };

export function SharedRecordingPage() {
    const { t } = useTranslation('recordings');
    const language = i18n.language;
    const { token = '' } = useParams();
    const [state, setState] = useState<SharedState>({ kind: 'loading' });
    const loadUrl = useCallback((download?: boolean) => recordingsApi.sharedUrl(token, download), [token]);

    useEffect(() => {
        let cancelled = false;
        recordingsApi
            .shared(token)
            .then((shared) => {
                if (!cancelled) setState({ kind: 'ready', shared });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                if (error instanceof ApiError && error.status === 401) {
                    location.assign(`/api/auth/login?returnTo=${encodeURIComponent(location.pathname)}`);
                    return;
                }
                if (error instanceof ApiError && error.status === 404) {
                    setState({ kind: 'notFound' });
                    return;
                }
                setState({ kind: 'error', message: error instanceof Error ? error.message : t('errors.unknown') });
            });
        return () => {
            cancelled = true;
        };
    }, [token, t]);

    const renderContent = () => {
        switch (state.kind) {
            case 'loading': {
                return (
                    <Group justify="center" py="xl">
                        <Loader />
                    </Group>
                );
            }
            case 'notFound': {
                return (
                    <Stack align="center" gap="sm" py="xl">
                        <IconLinkOff size={24} />
                        <Title order={3}>{t('shared.notFound')}</Title>
                        <Text c="dimmed" ta="center">
                            {t('shared.notFoundHint')}
                        </Text>
                        <Button component={Link} mt="md" to="/" variant="light">
                            {t('shared.home')}
                        </Button>
                    </Stack>
                );
            }
            case 'error': {
                return (
                    <Alert color="danger" icon={<IconAlertTriangle size={20} />} title={t('errors.url')}>
                        {state.message}
                    </Alert>
                );
            }
            case 'ready': {
                const { recording } = state.shared;
                return (
                    <Stack gap="md">
                        <Stack gap="var(--spacing-1)">
                            <Title order={2}>{recording.roomName}</Title>
                            <Text c="dimmed">
                                {formatDate(recording.startedAt, language)} · {formatRecordingDuration(recording.durationMs)}
                            </Text>
                        </Stack>
                        {recording.status === 'complete' ? (
                            <RecordingPlayer loadUrl={loadUrl} />
                        ) : (
                            <Alert color="warning" icon={<IconAlertTriangle size={20} />}>
                                {t('shared.notReady')}
                            </Alert>
                        )}
                    </Stack>
                );
            }
        }
    };

    return (
        <Container py="xl" size="md">
            <Stack gap="lg">
                <Group justify="space-between">
                    <Text c="dimmed" fw={500}>
                        {t('shared.title')}
                    </Text>
                    <Group gap="var(--spacing-2)" wrap="nowrap">
                        <LanguageSwitcher />
                        <UserMenu />
                    </Group>
                </Group>
                <Paper withBorder p="md">
                    {renderContent()}
                </Paper>
            </Stack>
        </Container>
    );
}
